import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { logEvent } from "firebase/analytics";
import { analytics } from "../../App/Analytics";
import {
  ExpandedPlaylistHeader,
  ExpandedPlaylistHeaderConfig,
} from "./ExpandedPlaylistHeader";

import { FetchPlaylistInfoV2 } from "../../graphql/customWebappQueries/playlistQueries";

import {
  FetchPlaylistInfoQuery,
  FetchPlaylistInfoQueryVariables,
} from "../../API";

import {
  LoadingView,
  ErrorView,
  CenteringView,
} from "../../Components/RecurringSmallViews/RecurringSmallViews";

import {
  ExpandedPlaylistTabSwitcher,
  ExpandedPlaylistTabSwitcherConfig,
} from "./ExpandedPlaylistTabSwitcher";

function _ExpandedPlaylistLoading(): JSX.Element {
  const { userHandle, postCollectionId } = useParams();

  logEvent(analytics, "screen_view", {
    firebase_screen: "ExpandedPlaylistLoading",
    firebase_screen_class: "ExpandedPlaylistLoading",
    user_handle: userHandle,
    post_collection_id: postCollectionId,
  });

  if (!userHandle || !postCollectionId) {
    return ErrorView();
  }

  const { loading, error, data } = useQuery<
    FetchPlaylistInfoQuery,
    FetchPlaylistInfoQueryVariables
  >(FetchPlaylistInfoV2, {
    errorPolicy: "ignore",
    variables: {
      postCollectionId,
    },
  });

  if (loading) {
    return LoadingView();
  }
  if (error) {
    return ErrorView();
  }

  const fetchedPlaylist = data?.getPostCollection;
  if (fetchedPlaylist) {
    logEvent(analytics, "screen_view", {
      firebase_screen: "ExpandedPlaylist_FetchedPlaylist",
      firebase_screen_class: "ExpandedPlaylistLoading",
      user_handle: userHandle,
      post_collection_id: postCollectionId,
      post_collection_name: fetchedPlaylist.name,
      post_collection_author_sub: fetchedPlaylist.author.sub,
      post_collection_author_name: fetchedPlaylist.author.name,
      post_collection_author_handle:
        fetchedPlaylist.author.userHandle?.renderedForm,
    });

    const headerConfig: ExpandedPlaylistHeaderConfig = {
      playlistName: fetchedPlaylist.name,
      playlistDescription: fetchedPlaylist.description,
      playlistAuthorUserHandle:
        fetchedPlaylist.author.userHandle?.renderedForm ?? "Unknown",
    };

    const tabSwitcherConfig: ExpandedPlaylistTabSwitcherConfig = {
      postCollectionId,
      defaultSortOrder: fetchedPlaylist.defaultSortOrder || undefined,
    };

    return (
      <div>
        <ExpandedPlaylistHeader {...headerConfig} />

        <ExpandedPlaylistTabSwitcher {...tabSwitcherConfig} />
      </div>
    );
  }

  return ErrorView("Unexpected.");
}

function ExpandedPlaylistLoading(): JSX.Element {
  return CenteringView(_ExpandedPlaylistLoading());
}

export default ExpandedPlaylistLoading;
